import { useState, useEffect } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import { supabase } from '../../lib/supabase'
import Modal from '../shared/Modal'
import TemplateForm from './TemplateForm'

export default function TemplatesTab({ selectedAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [deletingTemplate, setDeletingTemplate] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [filterAccountId, setFilterAccountId] = useState(selectedAccount?.PushAccountId || '')
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch accounts for filter dropdown and account names
  useEffect(() => {
    const fetchAccounts = async () => {
      const { data } = await supabase
        .from('PushAccounts')
        .select('PushAccountId, Name')
        .eq('IsDeleted', false)
        .order('Name')
      if (data) setAccounts(data)
    }
    fetchAccounts()
  }, [])

  // Update filter when selectedAccount changes
  useEffect(() => {
    if (selectedAccount) {
      setFilterAccountId(selectedAccount.PushAccountId)
    }
  }, [selectedAccount])

  const { data: templates, loading, error, refetch } = useSupabaseQuery('PushTemplates', {
    eq: { IsDeleted: false, ...(filterAccountId && { PushAccountId: filterAccountId }) },
    order: { column: 'Name', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('PushTemplates')

  // Get account name by ID
  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.PushAccountId === accountId)
    return account?.Name || 'Unknown'
  }

  // Filter templates by search
  const filteredTemplates = templates?.filter(template => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      template.Name?.toLowerCase().includes(search) ||
      template.Title?.toLowerCase().includes(search) ||
      template.Body?.toLowerCase().includes(search)
    )
  })

  const handleCreate = async (formData) => {
    const accountId = filterAccountId || selectedAccount?.PushAccountId
    if (!accountId) {
      return { error: { message: 'Please select an account first' } }
    }
    const { data, error } = await insert({
      PushAccountId: accountId,
      ...formData,
      IsDeleted: false
    })
    if (!error && data) {
      setShowCreateModal(false)
      refetch()
    }
    return { data, error }
  }

  const handleUpdate = async (formData) => {
    const { data, error } = await update(
      editingTemplate.PushTemplateId,
      formData,
      'PushTemplateId'
    )
    if (!error) {
      setEditingTemplate(null)
      if (selectedTemplate?.PushTemplateId === editingTemplate.PushTemplateId) {
        setSelectedTemplate({ ...selectedTemplate, ...formData })
      }
      refetch()
    }
    return { data, error }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingTemplate.PushTemplateId, 'PushTemplateId')
    if (!error) {
      if (selectedTemplate?.PushTemplateId === deletingTemplate.PushTemplateId) {
        setSelectedTemplate(null)
      }
      setDeletingTemplate(null)
      refetch()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Push Templates</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {filterAccountId ? `Templates for: ${getAccountName(Number(filterAccountId))}` : 'All templates'}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!filterAccountId && !selectedAccount}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          title={!filterAccountId && !selectedAccount ? 'Select an account to create templates' : ''}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Create Template</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Account Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Account
            </label>
            <select
              value={filterAccountId}
              onChange={(e) => {
                setFilterAccountId(e.target.value)
                setSelectedTemplate(null)
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Accounts</option>
              {accounts.map(account => (
                <option key={account.PushAccountId} value={account.PushAccountId}>
                  {account.Name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Templates ({filteredTemplates?.length || 0})
              </h3>
            </div>
            {filteredTemplates && filteredTemplates.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                {filteredTemplates.map((template) => (
                  <li
                    key={template.PushTemplateId}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-4 cursor-pointer transition ${
                      selectedTemplate?.PushTemplateId === template.PushTemplateId
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {template.Name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {template.Title}
                        </p>
                        {!filterAccountId && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            {getAccountName(template.PushAccountId)}
                          </p>
                        )}
                      </div>
                      {selectedTemplate?.PushTemplateId === template.PushTemplateId && (
                        <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No templates yet</p>
                {!filterAccountId && !selectedAccount && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Select an account to create templates
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Template Details */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {selectedTemplate.Name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Account: {getAccountName(selectedTemplate.PushAccountId)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
                    ID: {selectedTemplate.TemplateIdentifier}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingTemplate(selectedTemplate)}
                    className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingTemplate(selectedTemplate)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                    {selectedTemplate.Title || <span className="text-gray-400 italic">Not set</span>}
                  </p>
                </div>

                {/* Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Body
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded whitespace-pre-wrap">
                    {selectedTemplate.Body || <span className="text-gray-400 italic">Not set</span>}
                  </p>
                </div>

                {/* Click URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Click URL
                  </label>
                  {selectedTemplate.ClickUrl ? (
                    <a
                      href={selectedTemplate.ClickUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 bg-gray-50 dark:bg-gray-900 p-3 rounded block hover:underline break-all"
                    >
                      {selectedTemplate.ClickUrl}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-400 italic bg-gray-50 dark:bg-gray-900 p-3 rounded">
                      Not set
                    </p>
                  )}
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Created
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTemplate.Created).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Updated
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTemplate.Updated).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preview
                  </label>
                  <div className="bg-gray-900 text-white p-4 rounded-lg max-w-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{selectedTemplate.Title || 'Notification'}</p>
                        <p className="text-xs text-gray-300 mt-1">{selectedTemplate.Body || 'Message body'}</p>
                        {selectedTemplate.ClickUrl && (
                          <p className="text-xs text-blue-400 mt-1 truncate">{selectedTemplate.ClickUrl}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Template Selected</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select a template from the list to view details
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="Create Push Template" onClose={() => setShowCreateModal(false)}>
          <TemplateForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <Modal title="Edit Push Template" onClose={() => setEditingTemplate(null)}>
          <TemplateForm
            template={editingTemplate}
            onSubmit={handleUpdate}
            onCancel={() => setEditingTemplate(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTemplate && (
        <Modal title="Delete Template" onClose={() => setDeletingTemplate(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingTemplate.Name}</strong>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingTemplate(null)}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                {mutating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
